// src/pages/supervisor/MySpecialty.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    useTheme,
    Tooltip,
    Alert,
    LinearProgress,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useMediaQuery,
    Avatar,
    Divider,
    FormControlLabel,
    Switch,
    CircularProgress
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    School,
    Business,
    LocalHospital,
    WorkspacePremium,
    Info,
    Warning,
    Star,
    StarBorder,
    Close as CloseIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APISupervisorSpecialties,
    APISupervisorSpecialtyStore,
    APISupervisorSpecialtyUpdate,
    APISupervisorSpecialtyDelete,
    APISpecialtyByInstitution,
    APISpecialtyList,
    APISpecialtyStore
} from '../../apiServices/APIs';
import { 
    getUserDetails 
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import ErrorState from '../../components/ErrorState';
import LoadingState from '../../components/LoadingState';
import SupervisorBar from '../../components/SupervisorBar';
import { FullWidthContainer } from '../../components/FullWidthContainer';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const MySpecialty = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const showToast = useCustomToast();
    
    const [specialties, setSpecialties] = useState([]);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedSpecialty, setSelectedSpecialty] = useState(null);
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        years_of_experience: '',
        expertise_level: 'intermediate',
        notes: '',
        is_primary: false
    });
    const [remainingSlots, setRemainingSlots] = useState(10);
    const [maxAllowed] = useState(10);
    const [formErrors, setFormErrors] = useState({});
    
    const isMounted = useRef(true);
    const currentUserId = getUserDetails("Id");

    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        
        return () => {
            isMounted.current = false;
        };
    }, [showToast]);
    
    useEffect(() => {
        fetchMySpecialties();
    }, []);
    
    // Fetch my assigned specialties
    const fetchMySpecialties = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const result = await apiService.list(`${APISupervisorSpecialties}?supervisor_id=${currentUserId}`);
            
            if (result.success && isMounted.current) {
                setSpecialties(Array.isArray(result.data) ? result.data : []);
                setRemainingSlots(result.max_allowed - (result.count || 0));
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load your specialties");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, [currentUserId]);
    
    // Dialog close handlers with useCallback
    const handleCloseAddDialog = useCallback(() => {
        if (submitting) return; // Prevent closing while submitting
        setOpenAddDialog(false);
        setSelectedSpecialty(null);
        setPostStatus("ADD");
        setUpdateId('');
        setFormData({
            name: '',
            description: '',
            years_of_experience: '',
            expertise_level: 'intermediate',
            notes: '',
            is_primary: false
        });
        setFormErrors({});
    }, [submitting]);
    
    const handleCloseEditDialog = useCallback(() => {
        if (submitting) return; // Prevent closing while submitting
        setOpenEditDialog(false);
        setSelectedSpecialty(null);
        setPostStatus("ADD");
        setUpdateId('');
        setFormData({
            name: '',
            description: '',
            years_of_experience: '',
            expertise_level: 'intermediate',
            notes: '',
            is_primary: false
        });
        setFormErrors({});
    }, [submitting]);
    
    // Handle add specialty
    const handleOpenAddDialog = useCallback(() => {
        if (remainingSlots <= 0) {
            showToast("Limit Reached", `You can only select up to ${maxAllowed} specialties`, "warning");
            return;
        }
        
        setPostStatus("ADD");
        setUpdateId('');
        setFormData({
            name: '',
            description: '',
            years_of_experience: '',
            expertise_level: 'intermediate',
            notes: '',
            is_primary: false
        });
        setSelectedSpecialty(null);
        setFormErrors({});
        setOpenAddDialog(true);
    }, [remainingSlots, maxAllowed, showToast]);
    
    // Handle edit specialty
    const handleOpenEditDialog = useCallback((item) => {
        setPostStatus("UPDATE");
        setUpdateId(item.id);
        setFormData({
            name: item.specialty_name || '',
            description: item.description || '',
            years_of_experience: item.years_of_experience || '',
            expertise_level: item.expertise_level || 'intermediate',
            notes: item.notes || '',
            is_primary: item.is_primary || false
        });
        setSelectedSpecialty({
            id: item.specialty_id,
            name: item.specialty_name,
            description: item.description,
            university_name: item.university_name
        });
        setFormErrors({});
        setOpenEditDialog(true);
    }, []);
    
    const handleInputChange = useCallback((e) => {
        const { name, value, checked, type } = e.target;
        let newValue;
        
        if (type === 'checkbox') {
            newValue = checked;
        } else if (type === 'number') {
            newValue = value === '' ? '' : parseInt(value);
        } else {
            newValue = value;
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));
        
        // Clear error for the field being changed
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    }, [formErrors]);
    
    // Validate form
    const validateForm = useCallback(() => {
        const errors = {};
        
        if (!formData.name || !formData.name.trim()) {
            errors.name = 'Specialty name is required';
        }
        
        if (!formData.description || !formData.description.trim()) {
            errors.description = 'Specialty description is required';
        }
        
        if (formData.years_of_experience && (formData.years_of_experience < 0 || formData.years_of_experience > 50)) {
            errors.years_of_experience = 'Years of experience must be between 0 and 50';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData]);
    
    // Handle submit add (Two-step process)
    const handleSubmitAdd = useCallback(async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            showToast("Validation Error", "Please fill in all required fields", "warning");
            return;
        }
        
        setSubmitting(true);
        
        try {
            // Step 1: Create the specialty first
            const specialtyFormData = new FormData();
            specialtyFormData.append('name', formData.name.trim());
            specialtyFormData.append('description', formData.description.trim());
            
            const specialtyResult = await apiService.create(APISpecialtyStore, specialtyFormData);
            
            if (!specialtyResult || !specialtyResult.success) {
                showToast("Error", specialtyResult?.message || "Failed to create specialty", "error");
                setSubmitting(false);
                return;
            }
            
            // Get the newly created specialty ID
            const newSpecialtyId = specialtyResult.data?.id;
            
            if (!newSpecialtyId) {
                showToast("Error", "Failed to get specialty ID after creation", "error");
                setSubmitting(false);
                return;
            }
            
            // Step 2: Assign the specialty to the supervisor
            const supervisorFormData = new FormData();
            supervisorFormData.append('supervisor_id', currentUserId);
            supervisorFormData.append('specialty_id', newSpecialtyId);
            
            if (formData.years_of_experience) {
                supervisorFormData.append('years_of_experience', formData.years_of_experience);
            }
            supervisorFormData.append('expertise_level', formData.expertise_level);
            if (formData.notes) {
                supervisorFormData.append('notes', formData.notes);
            }
            supervisorFormData.append('is_primary', formData.is_primary ? '1' : '0');
            
            const supervisorResult = await apiService.create(APISupervisorSpecialtyStore, supervisorFormData);
            
            if (supervisorResult && supervisorResult.success && isMounted.current) {
                showToast("Success", "Specialty added successfully", "success");
                handleCloseAddDialog();
                fetchMySpecialties();
            } else {
                showToast("Error", supervisorResult?.message || "Failed to assign specialty", "error");
            }
        } catch (error) {
            console.error("API Error:", error);
            showToast("Error", error.response?.data?.message || "Operation failed", "error");
        } finally {
            if (isMounted.current) {
                setSubmitting(false);
            }
        }
    }, [formData, currentUserId, showToast, handleCloseAddDialog, fetchMySpecialties, validateForm]);
    
    // Handle submit edit
    const handleSubmitEdit = useCallback(async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            showToast("Validation Error", "Please fill in all required fields", "warning");
            return;
        }
        
        setSubmitting(true);
        
        const formDataToSend = new FormData();
        formDataToSend.append('updateId', updateId);
        
        // Include name and description for specialty update if needed
        formDataToSend.append('name', formData.name.trim());
        formDataToSend.append('description', formData.description.trim());
        
        if (formData.years_of_experience) {
            formDataToSend.append('years_of_experience', formData.years_of_experience);
        }
        formDataToSend.append('expertise_level', formData.expertise_level);
        if (formData.notes) {
            formDataToSend.append('notes', formData.notes);
        }
        formDataToSend.append('is_primary', formData.is_primary ? '1' : '0');
        
        try {
            const result = await apiService.update(APISupervisorSpecialtyUpdate, formDataToSend);
            
            if (result && result.success && isMounted.current) {
                showToast("Success", "Specialty updated successfully", "success");
                handleCloseEditDialog();
                fetchMySpecialties();
            } else if (result && !result.success) {
                showToast("Error", result.message || "Operation failed", "error");
            }
        } catch (error) {
            console.error("API Error:", error);
            showToast("Error", error.response?.data?.message || "Operation failed", "error");
        } finally {
            if (isMounted.current) {
                setSubmitting(false);
            }
        }
    }, [formData, updateId, showToast, handleCloseEditDialog, fetchMySpecialties, validateForm]);
    
    // Handle delete
    const handleDelete = useCallback(async (item) => {
        if (window.confirm(`Are you sure you want to remove "${item.specialty_name}" from your specialties?`)) {
            const formDataToSend = new FormData();
            formDataToSend.append('updateId', item.id);
            
            try {
                const result = await apiService.delete(APISupervisorSpecialtyDelete, formDataToSend);
                
                if (result && result.success && isMounted.current) {
                    showToast("Success", result.message, "success");
                    fetchMySpecialties();
                } else if (result && !result.success) {
                    showToast("Error", result.message || "Operation failed", "error");
                }
            } catch (error) {
                console.error("API Error:", error);
                showToast("Error", error.response?.data?.message || "Operation failed", "error");
            }
        }
    }, [showToast, fetchMySpecialties]);
    
    const getExpertiseLevelColor = useCallback((level) => {
        switch(level) {
            case 'expert': return 'success';
            case 'intermediate': return 'warning';
            case 'beginner': return 'info';
            default: return 'default';
        }
    }, []);
    
    const getExpertiseLevelIcon = useCallback((level) => {
        switch(level) {
            case 'expert': return <WorkspacePremium fontSize="small" />;
            case 'intermediate': return <Star fontSize="small" />;
            case 'beginner': return <StarBorder fontSize="small" />;
            default: return <Info fontSize="small" />;
        }
    }, []);
    
    if (loading) {
        return <LoadingState message="Loading Your Specialties..." variant="research" size="large" fullPage />;
    }
    
    if (error) {
        return <ErrorState error={error} onRetry={fetchMySpecialties} title="Unable to Load Specialties" variant="research" fullPage retryText="Retry Loading" />;
    }
    
    return (
        <>
            <FullWidthContainer>
                <Box sx={{ backgroundColor: 'white', borderRadius: 3 }}>
                    <SupervisorBar notificationCount={20} />
                    
                    {/* Header Section */}
                    <Paper elevation={2} sx={{ 
                        p: { xs: 2, sm: 3 }, 
                        mb: { xs: 2, sm: 3 }, 
                        borderRadius: 3,
                        backgroundColor: 'white',
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 2
                        }}>
                            <Box>
                                <Typography variant="h5" sx={{
                                    fontWeight: 'bold',
                                    color: theme.palette.primary.main,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }}>
                                    <LocalHospital />
                                    My Research Specialties
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Manage your areas of expertise and research interests
                                </Typography>
                            </Box>
                            
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAddDialog}
                                disabled={remainingSlots <= 0}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px'
                                }}
                            >
                                Add Specialty ({remainingSlots}/{maxAllowed} remaining)
                            </Button>
                        </Box>
                        
                        {/* Progress Bar */}
                        <Box sx={{ mt: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Specialties Selected
                                </Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {specialties.length} / {maxAllowed}
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={(specialties.length / maxAllowed) * 100}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: theme.palette.grey[200],
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 4,
                                        backgroundColor: specialties.length === maxAllowed ? 
                                            theme.palette.error.main : theme.palette.primary.main
                                    }
                                }}
                            />
                            {remainingSlots === 0 && (
                                <Alert severity="warning" sx={{ mt: 2 }} icon={<Warning />}>
                                    You have reached the maximum limit of {maxAllowed} specialties. Remove some to add new ones.
                                </Alert>
                            )}
                        </Box>
                    </Paper>
                    
                    {/* Specialties Grid */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <Paper elevation={2} sx={{ 
                            borderRadius: 3, 
                            p: { xs: 2, sm: 3 },
                            backgroundColor: 'white',
                        }}>
                            {specialties.length === 0 ? (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    py: { xs: 6, sm: 8 },
                                    color: 'text.secondary'
                                }}>
                                    <LocalHospital sx={{ fontSize: { xs: 48, sm: 60 }, mb: 2 }} />
                                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                        No Specialties Added Yet
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        Add your research specialties to get started
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={handleOpenAddDialog}
                                    >
                                        Add Your First Specialty
                                    </Button>
                                </Box>
                            ) : (
                                <Grid container spacing={3}>
                                    {specialties.map((item, index) => (
                                        <Grid item xs={12} sm={6} md={4} key={item.id}>
                                            <motion.div
                                                initial={{ opacity: 0, scale: 0.9 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ delay: index * 0.05 }}
                                            >
                                                <Card sx={{
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    position: 'relative',
                                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                                    '&:hover': {
                                                        transform: 'translateY(-4px)',
                                                        boxShadow: theme.shadows[8]
                                                    }
                                                }}>
                                                    {item.is_primary && (
                                                        <Chip
                                                            label="Primary"
                                                            color="primary"
                                                            size="small"
                                                            icon={<Star />}
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 10,
                                                                right: 10,
                                                                zIndex: 1
                                                            }}
                                                        />
                                                    )}
                                                    <CardContent>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                            <Avatar sx={{ 
                                                                bgcolor: theme.palette.primary.main,
                                                                mr: 2
                                                            }}>
                                                                <LocalHospital />
                                                            </Avatar>
                                                            <Box>
                                                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                                    {item.specialty_name}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Code: {item.specialty_code}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        
                                                        <Divider sx={{ my: 1.5 }} />
                                                        
                                                        {item.description && (
                                                            <Typography 
                                                                variant="body2" 
                                                                color="text.secondary" 
                                                                sx={{ 
                                                                    mb: 1.5,
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    display: '-webkit-box',
                                                                    WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical'
                                                                }}
                                                            >
                                                                {item.description}
                                                            </Typography>
                                                        )}
                                                        
                                                        <Box sx={{ mb: 1.5 }}>
                                                            <Chip
                                                                icon={getExpertiseLevelIcon(item.expertise_level)}
                                                                label={item.expertise_level_label}
                                                                color={getExpertiseLevelColor(item.expertise_level)}
                                                                size="small"
                                                                sx={{ mr: 1, mb: 1 }}
                                                            />
                                                            {item.years_of_experience && (
                                                                <Chip
                                                                    icon={<Business />}
                                                                    label={`${item.years_of_experience} years`}
                                                                    variant="outlined"
                                                                    size="small"
                                                                />
                                                            )}
                                                        </Box>
                                                        
                                                        {item.university_name && (
                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <School fontSize="small" />
                                                                {item.university_name}
                                                            </Typography>
                                                        )}
                                                        
                                                        {item.notes && (
                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                                                "{item.notes}"
                                                            </Typography>
                                                        )}
                                                    </CardContent>
                                                    
                                                    <Box sx={{ p: 2, pt: 0, mt: 'auto', display: 'flex', gap: 1 }}>
                                                        <Button
                                                            fullWidth
                                                            variant="outlined"
                                                            color="primary"
                                                            size="small"
                                                            startIcon={<EditIcon />}
                                                            onClick={() => handleOpenEditDialog(item)}
                                                            sx={{ borderRadius: 2 }}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            fullWidth
                                                            variant="outlined"
                                                            color="error"
                                                            size="small"
                                                            startIcon={<DeleteIcon />}
                                                            onClick={() => handleDelete(item)}
                                                            sx={{ borderRadius: 2 }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Box>
                                                </Card>
                                            </motion.div>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Paper>
                    </motion.div>
                </Box>
                
                {/* Add Specialty Dialog */}
                <Dialog
                    open={openAddDialog}
                    onClose={(event, reason) => {
                        // Prevent closing on backdrop click or escape key while submitting
                        if (reason === 'backdropClick' || submitting) {
                            return;
                        }
                        handleCloseAddDialog();
                    }}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    disableEscapeKeyDown={submitting}
                >
                    <DialogTitle sx={{ textAlign: 'center' }}>
                        <b>Add Research Specialty</b>
                        <br/>
                        <Typography variant="caption" display="block" color="text.secondary">
                            Create a new specialty and add it to your profile
                        </Typography>
                        {submitting && (
                            <IconButton
                                aria-label="close"
                                onClick={handleCloseAddDialog}
                                disabled
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 8,
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        )}
                    </DialogTitle>
                    <DialogContent>
                        <Box component="form" onSubmit={handleSubmitAdd} sx={{ mt: 2 }}>
                            <Box>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Specialty Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name || "Enter the name of your research specialty"}
                                        disabled={submitting}
                                    />
                                </Grid>
                                <br/>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Specialty Description"
                                        name="description"
                                        multiline
                                        rows={3}
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        error={!!formErrors.description}
                                        helperText={formErrors.description || "Describe this specialty in detail"}
                                        disabled={submitting}
                                    />
                                </Grid>
                                <br/>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Years of Experience"
                                        name="years_of_experience"
                                        type="number"
                                        value={formData.years_of_experience}
                                        onChange={handleInputChange}
                                        InputProps={{
                                            inputProps: { min: 0, max: 50 }
                                        }}
                                        error={!!formErrors.years_of_experience}
                                        helperText={formErrors.years_of_experience || "Optional: How many years have you worked in this field?"}
                                        disabled={submitting}
                                    />
                                </Grid>
                                <br/>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Expertise Level</InputLabel>
                                        <Select
                                            name="expertise_level"
                                            value={formData.expertise_level}
                                            onChange={handleInputChange}
                                            label="Expertise Level"
                                            disabled={submitting}
                                        >
                                            <MenuItem value="beginner">Beginner</MenuItem>
                                            <MenuItem value="intermediate">Intermediate</MenuItem>
                                            <MenuItem value="expert">Expert</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <br/>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Notes (Optional)"
                                        name="notes"
                                        multiline
                                        rows={3}
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        placeholder="Add any additional information about your expertise in this area..."
                                        disabled={submitting}
                                    />
                                </Grid>
                                <br/>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="is_primary"
                                                checked={formData.is_primary}
                                                onChange={handleInputChange}
                                                color="primary"
                                                disabled={submitting}
                                            />
                                        }
                                        label="Set as Primary Specialty"
                                    />
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                        Your primary specialty will be highlighted and shown prominently on your profile
                                    </Typography>
                                </Grid>
                            </Box>
                            
                            <DialogActions sx={{ mt: 3, px: 0 }}>
                                <Button
                                    color="error"
                                    onClick={handleCloseAddDialog}
                                    variant="contained"
                                    disabled={submitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    variant="contained"
                                    disabled={submitting || !formData.name || !formData.description}
                                    startIcon={submitting ? <CircularProgress size={20} /> : null}
                                >
                                    {submitting ? 'Adding...' : 'Add Specialty'}
                                </Button>
                            </DialogActions>
                        </Box>
                    </DialogContent>
                </Dialog>
                
                {/* Edit Specialty Dialog */}
                <Dialog
                    open={openEditDialog}
                    onClose={(event, reason) => {
                        // Prevent closing on backdrop click or escape key while submitting
                        if (reason === 'backdropClick' || submitting) {
                            return;
                        }
                        handleCloseEditDialog();
                    }}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    disableEscapeKeyDown={submitting}
                >
                    <DialogTitle sx={{ textAlign: 'center' }}>
                        <b>Edit Research Specialty</b>
                        <br/>
                        <Typography variant="caption" display="block" color="text.secondary">
                            Update your expertise details for {selectedSpecialty?.name}
                        </Typography>
                        {submitting && (
                            <IconButton
                                aria-label="close"
                                onClick={handleCloseEditDialog}
                                disabled
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 8,
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        )}
                    </DialogTitle>
                    <DialogContent>
                        <Box component="form" onSubmit={handleSubmitEdit} sx={{ mt: 2 }}>
                            <Box>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Specialty Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name || "Enter the name of your research specialty"}
                                        disabled={submitting}
                                    />
                                </Grid>
                                <br/>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        required
                                        label="Specialty Description"
                                        name="description"
                                        multiline
                                        rows={3}
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        error={!!formErrors.description}
                                        helperText={formErrors.description || "Describe this specialty in detail"}
                                        disabled={submitting}
                                    />
                                </Grid>
                                <br/>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Years of Experience"
                                        name="years_of_experience"
                                        type="number"
                                        value={formData.years_of_experience}
                                        onChange={handleInputChange}
                                        InputProps={{
                                            inputProps: { min: 0, max: 50 }
                                        }}
                                        error={!!formErrors.years_of_experience}
                                        helperText={formErrors.years_of_experience || "How many years have you worked in this field?"}
                                        disabled={submitting}
                                    />
                                </Grid>
                                <br/>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel>Expertise Level</InputLabel>
                                        <Select
                                            name="expertise_level"
                                            value={formData.expertise_level}
                                            onChange={handleInputChange}
                                            label="Expertise Level"
                                            disabled={submitting}
                                        >
                                            <MenuItem value="beginner">Beginner</MenuItem>
                                            <MenuItem value="intermediate">Intermediate</MenuItem>
                                            <MenuItem value="expert">Expert</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <br/>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Notes (Optional)"
                                        name="notes"
                                        multiline
                                        rows={3}
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        placeholder="Add any additional information about your expertise in this area..."
                                        disabled={submitting}
                                    />
                                </Grid>
                                <br/>
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="is_primary"
                                                checked={formData.is_primary}
                                                onChange={handleInputChange}
                                                color="primary"
                                                disabled={submitting}
                                            />
                                        }
                                        label="Set as Primary Specialty"
                                    />
                                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                                        Your primary specialty will be highlighted and shown prominently on your profile
                                    </Typography>
                                </Grid>
                            </Box>
                            
                            <DialogActions sx={{ mt: 3, px: 0 }}>
                                <Button
                                    color="error"
                                    onClick={handleCloseEditDialog}
                                    variant="contained"
                                    disabled={submitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    variant="contained"
                                    disabled={submitting || !formData.name || !formData.description}
                                    startIcon={submitting ? <CircularProgress size={20} /> : null}
                                >
                                    {submitting ? 'Updating...' : 'Update Specialty'}
                                </Button>
                            </DialogActions>
                        </Box>
                    </DialogContent>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default MySpecialty;